import {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import type { BraillerRepairRequestData, BraillerRepairResponse, BraillerCatalogItemDto } from '../brailler.types';
import type { ListItem } from '../../../../Utilities/Types/listUtilities';
import {PageTitle} from "../../../../Components/Landmarks";
import {locations} from "../../../../Utilities/Location";
import {braillerCatalogApi} from "../BraillerCatalog/braillerCatalogApi";
import {braillerRepairApi} from "../../../../SiteConfig/braillerRepairApi";
import {BraillerRepairRequestView} from "./BraillerRepairRequestView";
import {createBraillerRepairRequest} from "./braillerRepairRequestFactory";
import {useUserContext} from "../../../../Components/StateManager/Context/UserContext";
import {braillerRepairRequestApi} from "./braillerRepairRequestApi";
import {createListFromArrayOfObjects} from "../../../../Utilities/Types/listUtilities";
import {STUDENT_APPROVAL_STATUS, STUDENT_STATUS} from "../../../Student/studentConstants";
import {orderRequestApi} from "../../../Order/OrderRequest/orderRequestApi";
import {BraillerRepairDetailsModal} from "../BraillerHistory/BraillerRepairDetailsModal";
import {useBraillerRepairDetails} from "../Shared/useBraillerRepairDetails";
import {GridCell, GridContainer} from "../../../../Components/Grid";

export const BraillerRepairRequestContainer = ({
                                                   catalogItemId,
                                                   handleApiCall,
                                                   redirect,
                                                   user,
                                                   setWarnings,
                                                   setNotifications,
                                               }: ContextProps) => {
    const {user: currentUser} = useUserContext();
    const [studentId, setStudentId] = useState<string | null>(null);
    const [students, setStudents] = useState<ListItem[]>([]);
    const [catalogItem, setCatalogItem] = useState<BraillerCatalogItemDto | null>(null);
    const [loanedBraillers, setLoanedBraillers] = useState<Record<string, unknown>[]>([]);
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>([]);
    const [shippingLocationId, setShippingLocationId] = useState<string | null>(null);

    // Use the reusable hook for repair details modal
    const {
        isModalOpen,
        braillerLoanId,
        braillerRepairId,
        showRepairDetails,
        closeRepairDetails
    } = useBraillerRepairDetails(handleApiCall);

    const isFimcAdmin = (currentUser as Record<string, unknown>)?.roles && ((currentUser as Record<string, unknown>).roles as string[])?.includes("FimcAdmin");

    const getBraillerCatalogItem = (catalogItemId: string) => {
        handleApiCall(() => braillerCatalogApi.getBraillerCatalogItem(catalogItemId), (result: BraillerCatalogItemDto) => loadBraillerResult(result));
    }

    const loadBraillerResult = (result: BraillerCatalogItemDto) => {
        setCatalogItem(result);
    }

    const getStudents = () => {
        handleApiCall(() => braillerRepairRequestApi.getStudents({
            userId: user.userId,
            isActive: true,
            studentApprovalStatus: STUDENT_APPROVAL_STATUS.Approved.id,
            studentStatus: STUDENT_STATUS.Active.id,
            hasSignedParentalConsent: true,
            forDropDown: true,
        }), (results: Record<string, unknown>[]) => {
            setStudents(createListFromArrayOfObjects(results, `studentId`, `fullName`));
        });
    };

    const getShippingLocations = () => {
        handleApiCall(() => orderRequestApi.getShippingLocations(), (results: Record<string, unknown>[]) => {
            setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
        });
    };

    useEffect(() => {
        getStudents();
        getShippingLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (catalogItemId) {
            getBraillerCatalogItem(catalogItemId);
            setLoanedBraillers([]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalogItemId]);

    const handleSubmitRepairRequest = (repairData: BraillerRepairRequestData) => {
        const request = createBraillerRepairRequest(
            repairData.braillerCatalogItemId,
            repairData.issue,
            repairData.reportedBy,
            repairData.serialNumber,
            studentId,
            repairData.replacementPreference,
            repairData.noSerialNumber,
            repairData.braillerLoanId,
            repairData.shippingLocationId
        );

        handleApiCall(
            () => braillerRepairApi.requestRepair(request),
            (result: BraillerRepairResponse) => {
                if (setNotifications) {
                    setNotifications(["Repair request submitted successfully!"]);
                }

                const loanId = result.braillerLoanId;
                const repairId = result.id;

                if (loanId || repairId) {
                    showRepairDetails(loanId!, repairId ?? null);
                }
            },
            (error: { message?: string }) => {
                if (setWarnings) {
                    setWarnings([error.message || "Failed to submit repair request."]);
                }
            }
        );
    };

    const handleModalClose = () => {
        closeRepairDetails();
        redirect(locations.BRAILLER_CATALOG.path);
    };

    if (!catalogItem) return <></>;

    return (
        <>
            <PageTitle h1="Request Repair"/>
            <GridContainer>
                <GridCell>
                    <p>
                        If this brailler has a Paige Connect attached, please remove the Paige Connect and replace the
                        base prior to sending this brailler to APH.
                    </p>
                </GridCell>
            </GridContainer>
            <BraillerRepairRequestView
                catalogItem={catalogItem}
                loanedBraillers={loanedBraillers}
                user={user}
                onSubmit={handleSubmitRepairRequest as (data: Record<string, any>) => void}
                onCancel={() => redirect(locations.BRAILLER_CATALOG.path)}
                studentId={studentId}
                onStudentChange={setStudentId}
                students={students}
                isFimcAdmin={isFimcAdmin}
                shippingLocationId={shippingLocationId}
                onShippingLocationChange={setShippingLocationId}
                shippingLocations={shippingLocations}
                setWarnings={setWarnings}
            />

            <BraillerRepairDetailsModal
                braillerLoanId={braillerLoanId}
                braillerRepairId={braillerRepairId}
                isOpen={isModalOpen}
                onClose={handleModalClose}
                handleApiCall={handleApiCall}
            />
        </>
    );
};

